import React, { useState } from 'react';
import { seoData } from '@/data/seoTopics';
import { motion, AnimatePresence } from 'motion/react';
import { SEO } from '@/components/SEO';
import { cn } from '@/lib/utils';
import { 
  Calculator, Atom, FlaskConical, Dna,
  PieChart, BrainCircuit, Code2, HardHat,
  Cpu, Briefcase, Landmark, BookOpen,
  ChevronRight, ArrowLeft, Layers, Library
} from 'lucide-react';

export const TopicsPage = ({ onNavigate }: { onNavigate: (path: string) => void }) => {

  const getSubjectIcon = (subjectName: string) => {
    switch (true) {
      case subjectName.includes('Mathematics'): return <Calculator className="w-7 h-7" />;
      case subjectName.includes('Physics'): return <Atom className="w-7 h-7" />;
      case subjectName.includes('Chemistry'): return <FlaskConical className="w-7 h-7" />;
      case subjectName.includes('Biology'): return <Dna className="w-7 h-7" />;
      case subjectName.includes('Quantitative'): return <PieChart className="w-7 h-7" />;
      case subjectName.includes('Logical'): return <BrainCircuit className="w-7 h-7" />;
      case subjectName.includes('Computer'): return <Code2 className="w-7 h-7" />;
      case subjectName.includes('Civil'): return <HardHat className="w-7 h-7" />;
      case subjectName.includes('ECE'): return <Cpu className="w-7 h-7" />;
      case subjectName.includes('Commerce'): return <Briefcase className="w-7 h-7" />;
      case subjectName.includes('UPSC'): return <Landmark className="w-7 h-7" />;
      default: return <Library className="w-7 h-7" />;
    }
  };

  const getSubjectGradient = (index: number) => {
    const gradients = [
      "from-blue-500/20 to-indigo-500/20 text-blue-400 border-blue-500/30",
      "from-purple-500/20 to-pink-500/20 text-purple-400 border-purple-500/30",
      "from-emerald-500/20 to-teal-500/20 text-emerald-400 border-emerald-500/30",
      "from-orange-500/20 to-rose-500/20 text-orange-400 border-orange-500/30",
      "from-cyan-500/20 to-sky-500/20 text-cyan-400 border-cyan-500/30",
      "from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/30",
    ];
    return gradients[index % gradients.length];
  };

  return (
    <>
      <SEO 
        title="Explore Educational Topics & Subjects | Professor X AI"
        description="Browse thousands of curriculum topics and step-by-step visual AI explanations across JEE, NEET, CAT, GATE, CBSE, and Engineering subjects."
        canonical="https://professorxai.info/topics"
      />
      <div className="min-h-screen relative max-w-[1700px] mx-auto px-6 py-12 text-slate-200">
        <button 
          onClick={() => onNavigate('/')}
          className="group text-slate-400 hover:text-white flex items-center gap-2 font-medium mb-12 transition-colors relative z-10"
        >
          <div className="p-2 rounded-full bg-slate-800/50 group-hover:bg-slate-800 transition-colors border border-white/5">
            <ArrowLeft className="w-4 h-4" />
          </div>
          Back to Home
        </button>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20 text-center relative z-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-sm uppercase tracking-widest mb-6">
            <Layers className="w-4 h-4" />
            Master Directory
          </div>
          
          <h1 className="text-6xl sm:text-7xl font-black text-white mb-8 tracking-tight drop-shadow-lg">
            Browse Topics
          </h1>
          
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 max-w-4xl mx-auto">
            <div className="bg-slate-900/40 backdrop-blur-md rounded-2xl border border-white/10 p-6 flex-1 min-w-[200px] shadow-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="text-4xl font-black text-white mb-2 relative z-10">100+</p>
              <p className="text-indigo-400 font-medium tracking-wide text-sm uppercase relative z-10">Subjects Covered</p>
            </div>
            <div className="bg-slate-900/40 backdrop-blur-md rounded-2xl border border-white/10 p-6 flex-1 min-w-[200px] shadow-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <p className="text-4xl font-black text-white mb-2 relative z-10">1 Million+</p>
              <p className="text-purple-400 font-medium tracking-wide text-sm uppercase relative z-10">Practice Questions</p>
            </div>
            <div className="bg-slate-900/40 backdrop-blur-md rounded-2xl border border-white/10 p-6 flex-1 min-w-[200px] shadow-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex items-center justify-center">
                 <Layers className="w-9 h-9 text-white mb-3" />
              </div>
              <p className="text-emerald-400 font-medium tracking-wide text-sm uppercase relative z-10">All Types of Questions</p>
            </div>
          </div>
          
          <div className="mt-12 flex justify-center">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-indigo-500/30 p-1 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 shadow-[0_0_30px_rgba(79,70,229,0.2)] mx-auto relative z-10">
              <img src="/profxlogo.png" alt="Professor X AI" className="w-full h-full rounded-full object-cover bg-black" />
            </div>
          </div>
        </motion.div>

        {/* Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 relative z-10">
          {seoData.map((subject, idx) => {
            const styleClasses = getSubjectGradient(idx);
            const [bgGradient, textGradient, borderGradient] = styleClasses.split(' ');

            return (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                key={subject.subjectSlug} 
                className="group relative bg-[#0a0a0f]/80 backdrop-blur-xl border border-white/5 rounded-[2rem] overflow-hidden hover:border-white/20 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,0,0,0.5)] flex flex-col h-full cursor-pointer"
                onClick={() => onNavigate(`/explore/${subject.subjectSlug}`)}
              >
                {/* Card Header */}
                <div className={cn("p-8 relative overflow-hidden border-b border-white/5 bg-gradient-to-br", bgGradient)}>
                  <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 group-hover:opacity-20 transition-all duration-700 transform translate-x-4 -translate-y-4">
                    {getSubjectIcon(subject.subject)}
                  </div>
                  
                  <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg border backdrop-blur-sm", borderGradient, textGradient, "bg-white/5")}>
                    {getSubjectIcon(subject.subject)}
                  </div>
                  <h2 className="text-3xl font-black text-white tracking-tight relative z-10 drop-shadow-sm">
                    {subject.subject}
                  </h2>
                </div>

                {/* Card Body - Questions List */}
                <div className="p-8 flex-1 flex flex-col gap-6 relative">
                  {/* Background ambient glow inside card body on hover */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  {subject.subject === "Others" ? (
                    <ul className="space-y-3 relative z-10 w-full mb-2">
                      {subject.topics.map((topic) => (
                        <li key={topic.topicSlug} className="group/item">
                          <button 
                            onClick={(e) => { e.stopPropagation(); onNavigate(`/explore/${subject.subjectSlug}/${topic.topicSlug}`); }}
                            className="w-full text-left p-4 rounded-xl bg-slate-900/50 hover:bg-slate-800 border border-transparent hover:border-white/10 transition-all duration-300 flex items-start gap-3 shadow-sm"
                          >
                            <ChevronRight className="w-5 h-5 flex-shrink-0 text-slate-600 group-hover/item:text-indigo-400 group-hover/item:translate-x-1 transition-all mt-0.5" />
                            <span className="text-slate-300 group-hover/item:text-white leading-relaxed text-[15px] font-medium transition-colors line-clamp-3">
                              {topic.topic}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    subject.topics.map((topic, topicIdx) => (
                    <div key={topic.topicSlug} className="relative z-10">
                      <h3 className={cn("text-base font-bold uppercase tracking-widest mb-4 flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity", textGradient)} onClick={(e) => { e.stopPropagation(); onNavigate(`/explore/${subject.subjectSlug}/${topic.topicSlug}`); }}>
                        <div className="w-1.5 h-1.5 rounded-full bg-current" />
                        {topic.topic}
                      </h3>
                      <ul className="space-y-3">
                        {topic.questions.map((q) => (
                          <li key={q.slug} className="group/item">
                            <button 
                              onClick={(e) => { e.stopPropagation(); onNavigate(`/question/${q.slug}`); }}
                              className="w-full text-left p-4 rounded-xl bg-slate-900/50 hover:bg-slate-800 border border-transparent hover:border-white/10 transition-all duration-300 flex items-start gap-3 shadow-sm"
                            >
                              <ChevronRight className="w-5 h-5 flex-shrink-0 text-slate-600 group-hover/item:text-indigo-400 group-hover/item:translate-x-1 transition-all mt-0.5" />
                              <span className="text-slate-300 group-hover/item:text-white leading-relaxed text-[15px] font-medium transition-colors line-clamp-3">
                                {q.q}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </>
  );
};
